import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { BarChart2, CheckCircle, FileText, Info, List, Printer, RotateCcw } from "react-feather";
import DateRangePicker, { useDatePresetRange } from "../../../../components/common/DateRangePicker";
import ListExportToolbar from "../../../stock/components/list/ListExportToolbar";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { useEmployeePhotoCache } from "../../../masters/employees/useEmployeePhotoCache";
import {
  useGetPettyCashCategoriesQuery,
  useGetPettyCashFundsQuery,
  useGetPettyCashLedgerQuery,
  useListPettyCashVouchersQuery,
} from "../../pettyCashApi";
import { useCanViewLedger, useCanViewReports } from "../../usePettyCashPermissions";
import { PETTY_CASH_RECEIPT_FILTERS, PETTY_CASH_VOUCHER_STATUS_LABEL } from "../../pettyCashStatusMaps";
import { useAllPages } from "../../useAllPages";
import { groupByFundAndDay, fundPeriodLine, buildLedgerExport, LEDGER_EXPORT_COLUMNS } from "../../ledger/ledgerGrouping";
import LedgerTable, { LedgerLinesTable, ledgerLines, splitLedgerPages } from "../../ledger/components/LedgerTable";
import LedgerPrintModal from "../../ledger/components/LedgerPrintModal";
import {
  money,
  payeeKey,
  payeeName,
  voucherTotals,
  summarizeVouchers,
  SUMMARY_GROUPS,
  VOUCHER_REGISTER_EXPORT_COLUMNS,
} from "../../reports/voucherReport";
import OverviewSection from "../components/OverviewSection";
import { RegisterTable, SummaryTable, amt } from "../components/OverviewTables";

const ledgerIdOf = (r) => r.ledgerId;
const voucherIdOf = (r) => r.recordGuid;
const payeeGuidOf = (r) => r.paidToEmployeeRecordGuid;

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "PAID", label: "Paid" },
  { value: "APPROVED", label: "Approved" },
  { value: "SUBMITTED", label: "Submitted" },
  { value: "REJECTED", label: "Rejected" },
  { value: "CANCELLED", label: "Cancelled" },
];

// Typing in the amount/search boxes waits for a pause before it re-queries.
const useDebounced = (value, ms = 450) => {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return settled;
};

const toAmount = (v) => (v === "" || v == null || Number.isNaN(Number(v)) ? undefined : Number(v));

const Kpi = ({ label, value, tone, title, sub }) => (
  <div className="pc-kpi" title={title}>
    <div className="pc-kpi-label">{label}</div>
    <div className={`pc-kpi-value ${tone || ""}`}>{value}</div>
    {sub && <div className="pc-kpi-sub">{sub}</div>}
  </div>
);

const sumBy = (rows, pick) => rows.reduce((t, r) => t + Number(pick(r) || 0), 0);

/**
 * Petty Cash Overview (2026-09-15) - the Ledger, the Voucher Register and
 * the Summary on one screen under one set of filters, so a report is one
 * click away instead of one page away. UI only: each section reads the same
 * endpoint its own report page already uses.
 *
 * - Voucher filters (category, paid to, amount, status, receipt, search)
 *   narrow the Register, and the Summary is grouped from those same vouchers
 *   in the browser, so the two always agree.
 * - The Ledger follows only date, branch and fund: dropping entries would
 *   break the running balance. Under a voucher filter it highlights the
 *   matching payments/returns and dims the rest instead.
 * - Every section scrolls in its own box; the full-screen button opens it as
 *   screen-sized pages (OverviewSection / FitPagedView).
 */
const PettyCashOverviewPage = () => {
  const canLedger = useCanViewLedger();
  const canReports = useCanViewReports();
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const { preset, setPreset, dateFrom, setDateFrom, dateTo, setDateTo } = useDatePresetRange("THIS_MONTH");
  const [fundId, setFundId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [payee, setPayee] = useState(null);
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [status, setStatus] = useState("PAID");
  const [receiptStatus, setReceiptStatus] = useState("");
  const [search, setSearch] = useState("");
  const [groupBy, setGroupBy] = useState("category");
  const [showLedgerPrint, setShowLedgerPrint] = useState(false);

  const debouncedMin = useDebounced(minAmount);
  const debouncedMax = useDebounced(maxAmount);
  const debouncedSearch = useDebounced(search.trim());

  // Every fund, active or not - a closed fund still has history.
  // Only the header branch's funds (2026-10-02); a picked fund that is not in the new
  // branch is dropped so the register does not go silently empty.
  const { data: funds = [], isSuccess: fundsLoaded } = useGetPettyCashFundsQuery({ branchId: selectedBranchId ?? undefined });
  useEffect(() => {
    if (fundsLoaded && fundId && !funds.some((f) => String(f.fundId) === String(fundId))) setFundId("");
  }, [fundsLoaded, funds, fundId]);
  const { data: categories = [] } = useGetPettyCashCategoriesQuery();

  // ---- Ledger: date, branch, fund only ----
  const ledgerParams = useMemo(
    () => ({
      branchId: selectedBranchId ?? undefined,
      fundId: fundId || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [selectedBranchId, fundId, dateFrom, dateTo]
  );
  const ledger = useAllPages(useGetPettyCashLedgerQuery, ledgerParams, ledgerIdOf, { skip: !canLedger });
  const fundGroups = useMemo(() => groupByFundAndDay(ledger.items), [ledger.items]);
  const lines = useMemo(() => ledgerLines(fundGroups), [fundGroups]);

  // ---- Vouchers: every filter (paid to is applied here, below) ----
  const voucherParams = useMemo(
    () => ({
      branchId: selectedBranchId ?? undefined,
      fundId: fundId || undefined,
      categoryId: categoryId || undefined,
      status: status || undefined,
      receiptStatus: receiptStatus || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      search: debouncedSearch || undefined,
      minAmount: toAmount(debouncedMin),
      maxAmount: toAmount(debouncedMax),
    }),
    [selectedBranchId, fundId, categoryId, status, receiptStatus, dateFrom, dateTo, debouncedSearch, debouncedMin, debouncedMax]
  );
  const voucherFeed = useAllPages(useListPettyCashVouchersQuery, voucherParams, voucherIdOf, { skip: !canReports });

  // Paid to is picked from the people actually paid under the other filters.
  const payeeOptions = useMemo(() => {
    const seen = new Map();
    voucherFeed.items.forEach((r) => {
      const key = payeeKey(r);
      if (key && !seen.has(key)) seen.set(key, payeeName(r));
    });
    return [...seen.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [voucherFeed.items]);

  const vouchers = useMemo(
    () => (payee ? voucherFeed.items.filter((r) => payeeKey(r) === payee.value) : voucherFeed.items),
    [voucherFeed.items, payee]
  );
  const totals = useMemo(() => voucherTotals(vouchers), [vouchers]);
  const summary = useMemo(() => summarizeVouchers(vouchers, groupBy), [vouchers, groupBy]);
  const summaryTotals = useMemo(
    () =>
      summary.reduce(
        (t, s) => ({
          count: t.count + s.totalCount,
          requested: t.requested + s.totalAmount,
          paidCount: t.paidCount + s.paidCount,
          paid: t.paid + s.paidAmount,
          returned: t.returned + s.returnedAmount,
          net: t.net + s.net,
        }),
        { count: 0, requested: 0, paidCount: 0, paid: 0, returned: 0, net: 0 }
      ),
    [summary]
  );
  const summaryMaxNet = useMemo(() => Math.max(0, ...summary.map((s) => s.net)), [summary]);
  const photoUrls = useEmployeePhotoCache(vouchers, payeeGuidOf);

  // Ledger entries carry their voucher's number as the reference (payments
  // and returns alike). A voucher is matched on its expense date while the
  // ledger runs on payment date, so one expensed last month and paid this
  // month shows under the voucher's month in the register.
  const narrowsVouchers = !!(categoryId || payee || debouncedMin || debouncedMax || debouncedSearch || receiptStatus);
  const highlightRefs = useMemo(
    () => (narrowsVouchers && canReports ? new Set(vouchers.map((v) => v.voucherNumber)) : null),
    [narrowsVouchers, canReports, vouchers]
  );
  const highlightCount = useMemo(() => {
    if (!highlightRefs) return 0;
    return ledger.items.filter((r) => r.referenceType === "Voucher" && highlightRefs.has(r.referenceNumber)).length;
  }, [highlightRefs, ledger.items]);

  // Money in / out of the funds, split by what it was - so the ledger's
  // "Money out" is never read as the vouchers' "Paid": it also carries
  // adjustments, and it runs on payment date, not expense date.
  const ledgerKpi = useMemo(() => {
    const t = fundGroups.reduce(
      (a, f) => ({ opening: a.opening + f.periodOpening, closing: a.closing + f.periodClosing }),
      { opening: 0, closing: 0 }
    );
    const of = (type, side) => sumBy(ledger.items.filter((r) => r.transactionType === type), (r) => r[side]);
    return {
      ...t,
      inTotal: sumBy(ledger.items, (r) => r.creditAmount),
      outTotal: sumBy(ledger.items, (r) => r.debitAmount),
      refills: of("REFILL_ISSUE", "creditAmount"),
      returnsIn: of("VOUCHER_RETURN", "creditAmount"),
      adjustIn: of("ADJUSTMENT_IN", "creditAmount") + of("OPENING_BALANCE", "creditAmount"),
      voucherOut: of("VOUCHER_PAYMENT", "debitAmount"),
      adjustOut: of("ADJUSTMENT_OUT", "debitAmount"),
    };
  }, [fundGroups, ledger.items]);

  // Paid vouchers vs the ledger's voucher payments. They differ only by
  // timing - the register goes by a voucher's expense date, the ledger by the
  // day it was paid - so the gap is explained voucher by voucher. Shown only
  // without voucher filters (the ledger cannot be narrowed the same way) and
  // while paid vouchers are in view.
  const reconciliation = useMemo(() => {
    if (!canLedger || !canReports || narrowsVouchers || (status && status !== "PAID")) return null;
    if (!ledger.isComplete || !voucherFeed.isComplete) return null;
    const ledgerPaid = new Map();
    ledger.items
      .filter((r) => r.transactionType === "VOUCHER_PAYMENT" && r.referenceNumber)
      .forEach((r) => ledgerPaid.set(r.referenceNumber, (ledgerPaid.get(r.referenceNumber) || 0) + Number(r.debitAmount || 0)));
    const paidVouchers = vouchers.filter((v) => v.status === "PAID");
    const registerNos = new Set(paidVouchers.map((v) => v.voucherNumber));
    const paidElsewhere = paidVouchers.filter((v) => !ledgerPaid.has(v.voucherNumber));
    const datedElsewhere = [...ledgerPaid.entries()].filter(([no]) => !registerNos.has(no));
    const ledgerTotal = [...ledgerPaid.values()].reduce((a, b) => a + b, 0);
    const registerTotal = sumBy(paidVouchers, (v) => v.paidAmount);
    return {
      ledgerTotal,
      registerTotal,
      agrees: Math.abs(ledgerTotal - registerTotal) < 0.005,
      paidElsewhere: {
        count: paidElsewhere.length,
        amount: sumBy(paidElsewhere, (v) => v.paidAmount),
        numbers: paidElsewhere.map((v) => v.voucherNumber),
      },
      datedElsewhere: {
        count: datedElsewhere.length,
        amount: datedElsewhere.reduce((a, [, x]) => a + x, 0),
        numbers: datedElsewhere.map(([no]) => no),
      },
    };
  }, [canLedger, canReports, narrowsVouchers, status, ledger.isComplete, ledger.items, voucherFeed.isComplete, vouchers]);

  // ---- What the filters say, for every printout and the full-screen views ----
  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;
  const selectedFund = funds.find((f) => String(f.fundId) === String(fundId));
  const selectedCategory = categories.find((c) => String(c.categoryId) === String(categoryId));
  const voucherFilterLines = [
    `Branch: ${branchCode || "All"}`,
    `Period: ${dateFrom} to ${dateTo}`,
    selectedFund && `Fund: ${selectedFund.fundName}`,
    selectedCategory && `Category: ${selectedCategory.categoryName}`,
    payee && `Paid to: ${payee.label}`,
    (debouncedMin || debouncedMax) && `Amount: ${debouncedMin || "0"} to ${debouncedMax || "any"}`,
    `Status: ${status ? PETTY_CASH_VOUCHER_STATUS_LABEL[status] || status : "All"}`,
    receiptStatus && `Receipt: ${(PETTY_CASH_RECEIPT_FILTERS.find((o) => o.value === receiptStatus) || {}).label}`,
    debouncedSearch && `Search: "${debouncedSearch}"`,
  ].filter(Boolean);
  const ledgerFilterLines = [`Branch: ${branchCode || "All"}`, `Fund: ${selectedFund ? selectedFund.fundName : "All funds"}`, `Period: ${dateFrom} to ${dateTo}`];
  const exportBranchCode = selectedFund?.branchCode || branchCode;

  const ledgerReady = ledger.isComplete && !ledger.isFetching && fundGroups.length > 0;
  const vouchersReady = voucherFeed.isComplete && !voucherFeed.isFetching;
  const { exportRows: ledgerExportRows, groupOrder: ledgerGroupOrder } = useMemo(() => buildLedgerExport(fundGroups), [fundGroups]);
  const groupLabel = SUMMARY_GROUPS.find((g) => g.value === groupBy)?.label || "Group";

  const summaryExportColumns = [
    { header: groupLabel, value: (s) => s.label },
    { header: "Vouchers", value: (s) => s.totalCount },
    { header: "Requested", value: (s) => money(s.totalAmount), align: "right" },
    { header: "Paid", value: (s) => s.paidCount },
    { header: "Paid Amount", value: (s) => money(s.paidAmount), align: "right" },
    { header: "Returned", value: (s) => money(s.returnedAmount), align: "right" },
    { header: "Net Spend", value: (s) => money(s.net), align: "right" },
  ];

  // A Summary row narrows the whole page to its category / person / fund.
  const pickGroup = (s) => {
    if (groupBy === "category") setCategoryId(String(s.filterValue));
    else if (groupBy === "payee") setPayee({ value: s.filterValue, label: s.label });
    else if (groupBy === "fund") setFundId(String(s.filterValue));
  };

  const resetFilters = () => {
    setPreset("THIS_MONTH");
    setFundId("");
    setCategoryId("");
    setPayee(null);
    setMinAmount("");
    setMaxAmount("");
    setStatus("PAID");
    setReceiptStatus("");
    setSearch("");
  };

  const loadingNote = (feed) => (feed.isLoading ? "Loading..." : !feed.isComplete ? `Loading all ${feed.totalCount}...` : null);

  return (
    <div className="page-wrapper">
      <div className="content">
        {ledger.loaders}
        {voucherFeed.loaders}

        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Petty Cash Overview</h4>
              <h6>Ledger, Voucher Register and Summary on one screen - one set of filters drives all three</h6>
            </div>
          </div>
        </div>

        <div className="card pc-filters">
          <div className="card-body">
            <DateRangePicker preset={preset} setPreset={setPreset} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
            <div className="pc-filter-row">
              <select className="form-select form-select-sm" value={fundId} onChange={(e) => setFundId(e.target.value)} title="Fund">
                <option value="">All funds</option>
                {funds.map((f) => (
                  <option key={f.fundId} value={f.fundId}>
                    {f.fundName} ({f.branchCode})
                  </option>
                ))}
              </select>
              {canReports && (
                <>
                  <select className="form-select form-select-sm" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} title="Category">
                    <option value="">All categories</option>
                    {categories.map((c) => (
                      <option key={c.categoryId} value={c.categoryId}>
                        {c.categoryName}
                      </option>
                    ))}
                  </select>
                  <div className="pc-filter-payee">
                    <Select
                      classNamePrefix="react-select"
                      options={payeeOptions}
                      value={payee}
                      onChange={setPayee}
                      isClearable
                      placeholder={`Paid to (${payeeOptions.length})`}
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }), control: (base) => ({ ...base, minHeight: 31 }) }}
                    />
                  </div>
                  <div className="input-group input-group-sm pc-filter-amount" title="Amount (paid, else approved, else requested)">
                    <input type="number" min="0" className="form-control" placeholder="Min amount" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} />
                    <span className="input-group-text">to</span>
                    <input type="number" min="0" className="form-control" placeholder="Max" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} />
                  </div>
                  <select className="form-select form-select-sm" value={status} onChange={(e) => setStatus(e.target.value)} title="Voucher status">
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <select className="form-select form-select-sm" value={receiptStatus} onChange={(e) => setReceiptStatus(e.target.value)} title="Receipt">
                    {PETTY_CASH_RECEIPT_FILTERS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    className="form-control form-control-sm pc-filter-search"
                    placeholder="Search voucher no, description, supplier..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </>
              )}
              <button type="button" className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1" onClick={resetFilters}>
                <RotateCcw size={13} /> Reset
              </button>
            </div>

            <div className="pc-kpi-row">
              {canReports && (
                <div className="pc-kpi-group">
                  <div className="pc-kpi-group-title" title="Vouchers whose expense date falls in the period, under all the filters above">
                    Vouchers &middot; by expense date
                  </div>
                  <div className="pc-kpi-group-items">
                    <Kpi label="Vouchers" value={totals.count} />
                    <Kpi label="Requested" value={amt(totals.requested)} />
                    <Kpi label="Paid" value={amt(totals.paid)} tone="good" />
                    <Kpi label="Returned" value={amt(totals.returned)} />
                    <Kpi label="Net spend" value={amt(totals.paid - totals.returned)} tone="strong" />
                  </div>
                </div>
              )}
              {canLedger && fundGroups.length > 0 && (
                <div className="pc-kpi-group ledger">
                  <div className="pc-kpi-group-title" title="Every movement of fund cash dated in the period - date, branch and fund filters only">
                    Fund ledger &middot; by payment date
                  </div>
                  <div className="pc-kpi-group-items">
                    <Kpi label="Opening" value={amt(ledgerKpi.opening)} title="Funds with entries in this period" />
                    <Kpi
                      label="Money in"
                      value={amt(ledgerKpi.inTotal)}
                      sub={[
                        ledgerKpi.refills && `Refills ${amt(ledgerKpi.refills)}`,
                        ledgerKpi.returnsIn && `Returns ${amt(ledgerKpi.returnsIn)}`,
                        ledgerKpi.adjustIn && `Adj. ${amt(ledgerKpi.adjustIn)}`,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    />
                    <Kpi
                      label="Money out"
                      value={amt(ledgerKpi.outTotal)}
                      sub={[ledgerKpi.voucherOut && `Vouchers ${amt(ledgerKpi.voucherOut)}`, ledgerKpi.adjustOut && `Adj. ${amt(ledgerKpi.adjustOut)}`]
                        .filter(Boolean)
                        .join(" · ")}
                    />
                    <Kpi label="Closing" value={amt(ledgerKpi.closing)} tone="strong" title="Funds with entries in this period" />
                  </div>
                </div>
              )}
            </div>

            {reconciliation && (
              <div className={`pc-recon ${reconciliation.agrees ? "ok" : "gap"}`}>
                {reconciliation.agrees ? (
                  <>
                    <CheckCircle size={14} />
                    <span>
                      Voucher payments agree: {amt(reconciliation.registerTotal)} paid on the vouchers above = {amt(reconciliation.ledgerTotal)} voucher payments in the
                      ledger.
                    </span>
                  </>
                ) : (
                  <>
                    <Info size={14} />
                    <span>
                      Paid on these vouchers <b>{amt(reconciliation.registerTotal)}</b> vs voucher payments in the ledger <b>{amt(reconciliation.ledgerTotal)}</b> - a
                      timing difference only:
                      {reconciliation.paidElsewhere.count > 0 && (
                        <span className="pc-recon-part" title={reconciliation.paidElsewhere.numbers.join(", ")}>
                          {" "}
                          {reconciliation.paidElsewhere.count} voucher{reconciliation.paidElsewhere.count === 1 ? "" : "s"} dated in this period but paid outside it (
                          {amt(reconciliation.paidElsewhere.amount)})
                        </span>
                      )}
                      {reconciliation.paidElsewhere.count > 0 && reconciliation.datedElsewhere.count > 0 && ";"}
                      {reconciliation.datedElsewhere.count > 0 && (
                        <span className="pc-recon-part" title={reconciliation.datedElsewhere.numbers.join(", ")}>
                          {" "}
                          {reconciliation.datedElsewhere.count} payment{reconciliation.datedElsewhere.count === 1 ? "" : "s"} in this period for vouchers dated outside it (
                          {amt(reconciliation.datedElsewhere.amount)})
                        </span>
                      )}
                      . Hover for the voucher numbers.
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {canLedger && (
          <OverviewSection
            title="Ledger"
            icon={List}
            count={ledger.items.length}
            status={
              loadingNote(ledger) ||
              (highlightRefs ? `${highlightCount} matching entr${highlightCount === 1 ? "y" : "ies"} highlighted - date and fund filters only` : null)
            }
            subtitle={ledgerFilterLines.join(" · ")}
            height="44vh"
            emptyText={ledger.isLoading ? "Loading..." : "No ledger entries for this period."}
            actions={
              <>
                <button type="button" className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1" disabled={!ledgerReady} onClick={() => setShowLedgerPrint(true)}>
                  <Printer size={14} /> Print
                </button>
                {ledgerReady && (
                  <ListExportToolbar
                    title="Petty Cash Ledger"
                    subtitleLines={[...ledgerFilterLines, ...fundGroups.map(fundPeriodLine)]}
                    branchCode={exportBranchCode}
                    columns={LEDGER_EXPORT_COLUMNS}
                    rows={ledgerExportRows}
                    groupBy={(x) => x.group}
                    groupOrder={ledgerGroupOrder}
                    fileName={`PettyCashLedger_${dateFrom}_to_${dateTo}`}
                  />
                )}
              </>
            }
            renderSection={() => <LedgerTable fundGroups={fundGroups} highlightRefs={highlightRefs} sticky />}
            fit={{
              items: lines,
              split: splitLedgerPages,
              noun: "ledger lines",
              renderPage: (pageLines) => <LedgerLinesTable lines={pageLines} highlightRefs={highlightRefs} />,
            }}
          />
        )}

        {canReports && (
          <OverviewSection
            title="Voucher Register"
            icon={FileText}
            count={vouchers.length}
            status={loadingNote(voucherFeed) || (voucherFeed.isCapped ? `first ${voucherFeed.cap} only - narrow the period` : null)}
            subtitle={voucherFilterLines.join(" · ")}
            height="44vh"
            emptyText={voucherFeed.isLoading ? "Loading..." : "No vouchers match these filters."}
            actions={
              vouchersReady && vouchers.length > 0 ? (
                <ListExportToolbar
                  title="Petty Cash Voucher Register"
                  subtitleLines={voucherFilterLines}
                  branchCode={branchCode}
                  columns={VOUCHER_REGISTER_EXPORT_COLUMNS}
                  rows={vouchers}
                  fileName={`PettyCashVoucherRegister_${dateFrom}_to_${dateTo}`}
                />
              ) : null
            }
            renderSection={() => <RegisterTable rows={vouchers} totals={totals} showBranch={selectedBranchId == null} photoUrls={photoUrls} />}
            fit={{
              items: vouchers,
              noun: "vouchers",
              renderPage: (pageRows) => <RegisterTable rows={pageRows} totals={totals} showBranch={selectedBranchId == null} photoUrls={photoUrls} />,
            }}
          />
        )}

        {canReports && (
          <OverviewSection
            title="Summary"
            icon={BarChart2}
            count={summary.length}
            status={loadingNote(voucherFeed)}
            subtitle={voucherFilterLines.join(" · ")}
            height="36vh"
            emptyText={voucherFeed.isLoading ? "Loading..." : "No vouchers match these filters."}
            headerExtra={
              <select className="form-select form-select-sm" style={{ width: "auto" }} value={groupBy} onChange={(e) => setGroupBy(e.target.value)} title="Group by">
                {SUMMARY_GROUPS.map((g) => (
                  <option key={g.value} value={g.value}>
                    By {g.label}
                  </option>
                ))}
              </select>
            }
            actions={
              vouchersReady && summary.length > 0 ? (
                <ListExportToolbar
                  title={`Petty Cash Summary - by ${groupLabel}`}
                  subtitleLines={voucherFilterLines}
                  branchCode={branchCode}
                  columns={summaryExportColumns}
                  rows={summary}
                  fileName={`PettyCashSummary_${groupBy}_${dateFrom}_to_${dateTo}`}
                />
              ) : null
            }
            renderSection={() => (
              <SummaryTable rows={summary} groupLabel={groupLabel} groupBy={groupBy} grandTotals={summaryTotals} onPick={pickGroup} photoUrls={photoUrls} maxNet={summaryMaxNet} />
            )}
            fit={{
              items: summary,
              noun: "groups",
              renderPage: (pageRows) => (
                <SummaryTable rows={pageRows} groupLabel={groupLabel} groupBy={groupBy} grandTotals={summaryTotals} onPick={pickGroup} photoUrls={photoUrls} maxNet={summaryMaxNet} />
              ),
            }}
          />
        )}

        {showLedgerPrint && (
          <LedgerPrintModal show={showLedgerPrint} onClose={() => setShowLedgerPrint(false)} fundGroups={fundGroups} dateFrom={dateFrom} dateTo={dateTo} />
        )}
      </div>
    </div>
  );
};

export default PettyCashOverviewPage;
